<template>
    <v-dialog v-model="internalDialog" persistent max-width="640" scrollable content-class="sod-eod-dialog sod-dialog-content">
        <v-card class="sod-eod-card">
            <div class="sod-eod-header">
                <div class="sod-eod-badge sod-badge">☀️</div>
                <div class="sod-eod-heading">
                    <div class="sod-eod-title">{{ dialogTitle }}</div>
                    <div class="sod-eod-subtitle">Let's plan your day.</div>
                </div>
                <button type="button" class="sod-eod-close" @click="dismiss" aria-label="Close">
                    <v-icon small>mdi-close</v-icon>
                </button>
            </div>

            <v-card-text class="sod-eod-body">
                <v-alert v-if="supersededNotice" dense outlined type="warning" class="mb-4">
                    A newer Start of Day submission already exists (made from another tab or device).
                    Showing that submission below — resubmitting will update it further.
                </v-alert>

                <v-progress-circular v-if="loading" indeterminate color="#2E9E5B" class="d-block mx-auto my-6" />

                <v-form v-else ref="sodForm">
                    <div class="sod-eod-info-box sod-info-box">
                        <div class="sod-eod-info-title">What do you want to accomplish today?</div>
                        <div class="sod-eod-info-text">
                            Select tasks from your list or add a new one. These will be marked as your planned tasks for today.
                        </div>
                    </div>

                    <div v-if="displayedTasks.length === 0" class="text-body-2 grey--text mb-3">
                        No open tasks found. Add one below to get your day started.
                    </div>

                    <div v-for="task in displayedTasks" :key="task.id" class="sod-task-row">
                        <v-checkbox v-model="selection(task.id).selected" hide-details dense
                            class="mt-0 pt-0 sod-task-checkbox" color="#2E9E5B">
                            <template v-slot:label>
                                <span class="text-body-2">{{ task.name }}</span>
                                <v-chip v-if="task.status_id === 4" x-small label color="success" outlined class="ml-2">
                                    Completed
                                </v-chip>
                            </template>
                        </v-checkbox>
                        <v-select v-model="selection(task.id).priority_tier" :items="priorityTierOptions"
                            item-text="text" item-value="value" dense outlined hide-details rounded
                            :disabled="!selection(task.id).selected" class="sod-task-priority">
                            <template v-slot:selection="{ item }">
                                <span class="tier-dot" :style="{ background: tierColor(item.value) }"></span>
                                <span class="tier-label">{{ item.text }}</span>
                            </template>
                            <template v-slot:item="{ item }">
                                <span class="tier-dot" :style="{ background: tierColor(item.value) }"></span>
                                <span class="tier-label">{{ item.text }}</span>
                            </template>
                        </v-select>
                    </div>

                    <button type="button" class="sod-eod-link mt-2 mb-4" @click="showAddTask = !showAddTask">
                        + Add another task
                    </button>
                    <div v-if="showAddTask" class="d-flex align-center mb-4" style="gap: 8px;">
                        <v-text-field v-model="newTaskName" placeholder="Task name" dense outlined rounded
                            hide-details :loading="addingTask" autofocus @keyup.enter="addQuickTask" />
                        <v-btn icon color="#2E9E5B" :loading="addingTask" :disabled="!newTaskName.trim()"
                            @click="addQuickTask">
                            <v-icon>mdi-plus</v-icon>
                        </v-btn>
                    </div>

                    <div class="sod-eod-info-title mb-2">Today's focus (optional)</div>
                    <v-textarea v-model="focusNote" outlined dense rows="2" hide-details rounded
                        placeholder="What's the one thing that matters most today?" />
                </v-form>
            </v-card-text>

            <v-card-actions class="sod-eod-actions">
                <v-btn v-if="isAdmin" @click="clearData" :loading="clearing" :disabled="loading || saving || clearing"
                    text color="error" class="text-none">
                    Clear Data
                </v-btn>
                <v-btn @click="viewHistory" :disabled="saving || clearing" text color="primary" class="text-none">
                    View History
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn @click="dismiss" :disabled="saving || clearing" class="sod-eod-btn-secondary" depressed>Cancel</v-btn>
                <v-btn @click="submit" :loading="saving" :disabled="loading || clearing" class="sod-eod-btn-primary" depressed>
                    Start My Day
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name: 'SodDialog',
    inject: {
        personalTasks: { from: 'personalTasks', default: () => () => [] },
        assignedTasks: { from: 'assignedTasks', default: () => () => [] },
        todaySod: { from: 'todaySod', default: () => () => null },
        refreshTodaySod: { from: 'refreshTodaySod', default: () => async () => null },
        refreshTasks: { from: 'refreshTasks', default: () => async () => null },
        submitSodAction: { from: 'submitSod', default: () => async () => { throw new Error('submitSod not provided'); } },
        clearSodAction: { from: 'clearSod', default: () => async () => { throw new Error('clearSod not provided'); } },
        sodEodStatus: { from: 'sodEodStatus', default: () => () => null },
    },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            saving: false,
            clearing: false,
            addingTask: false,
            showAddTask: false,
            newTaskName: '',
            focusNote: '',
            taskSelections: {}, // { [task_id]: { selected: Boolean, priority_tier: 1|2|3, was_added_inline: Boolean } }
            inlineCreatedTasks: [], // tasks created via "+ Add another task" this session, in case refreshTasks hasn't landed yet
            supersededNotice: null,
            clientUuid: null,
            // Mirrors the Tracking App's TierLabelToColorConverter palette.
            priorityTierOptions: [
                { value: 1, text: 'Must Do', color: '#E5484D' },
                { value: 2, text: 'Should Do', color: '#F5A623' },
                { value: 3, text: 'If Time Allows', color: '#2E9E5B' },
            ],
        };
    },
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user',
        }),
        internalDialog: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit('input', val);
            },
        },
        dialogTitle() {
            const existing = this.todaySod();
            return existing ? 'View Start of Day' : 'Start My Day';
        },
        isAdmin() {
            return !!this.get_user && this.get_user.role_id === 1;
        },
        availableTasks() {
            const personal = (this.personalTasks() || []);
            const assigned = (this.assignedTasks() || []);
            const merged = [...personal, ...assigned, ...this.inlineCreatedTasks];
            const seen = new Set();
            const result = [];
            for (const task of merged) {
                if (!task || seen.has(task.id)) continue;
                seen.add(task.id);
                // Skip already-completed tasks — SOD planning is for what's still open.
                if (task.status_id === 4) continue;
                result.push(task);
            }
            return result;
        },
        // availableTasks plus any task already planned in today's SOD that is
        // no longer open (e.g. completed since) — those must stay visible and
        // selected so resubmitting never drops them from the SOD/EOD.
        displayedTasks() {
            const rows = [...this.availableTasks];
            const shown = new Set(rows.map((t) => t.id));
            const all = [...(this.personalTasks() || []), ...(this.assignedTasks() || [])];
            const existing = this.todaySod();
            ((existing && existing.tasks) || []).forEach((t) => {
                if (shown.has(t.task_id)) return;
                shown.add(t.task_id);
                const known = all.find((x) => x && x.id === t.task_id);
                rows.push(known || { id: t.task_id, name: t.name || 'Unknown task' });
            });
            return rows;
        },
    },
    watch: {
        value(open) {
            if (open) {
                this.resetForm();
            }
        },
        // Seed a default (unselected) entry for any task that doesn't have one yet
        // as soon as it appears in the list — keeps the $set (reactive key-add)
        // out of the render path, since v-model can't create new reactive keys.
        // Also re-runs resetForm's own seeding for tasks the SOD prefill didn't
        // already cover (see seedMissingSelections).
        availableTasks: {
            immediate: true,
            handler(tasks) {
                this.seedMissingSelections(tasks);
            },
        },
    },
    methods: {
        tierColor(value) {
            const match = this.priorityTierOptions.find((opt) => opt.value === value);
            return match ? match.color : '#9AA0A6';
        },
        // Read-only accessor for the template — seeding happens via
        // seedMissingSelections, never as a render-time side effect.
        selection(taskId) {
            return this.taskSelections[taskId] || { selected: false, priority_tier: 2, was_added_inline: false };
        },
        seedMissingSelections(tasks) {
            (tasks || this.availableTasks).forEach((task) => {
                if (!this.taskSelections[task.id]) {
                    this.$set(this.taskSelections, task.id, { selected: false, priority_tier: 2, was_added_inline: false });
                }
            });
        },
        generateUuid() {
            if (window.crypto && window.crypto.randomUUID) {
                return window.crypto.randomUUID();
            }
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
                const r = (Math.random() * 16) | 0;
                const v = c === 'x' ? r : (r & 0x3) | 0x8;
                return v.toString(16);
            });
        },
        async resetForm() {
            this.supersededNotice = null;
            this.newTaskName = '';
            this.showAddTask = false;
            this.inlineCreatedTasks = [];
            this.taskSelections = {};
            this.clientUuid = this.generateUuid();
            this.loading = true;
            try {
                await this.refreshTodaySod();
            } finally {
                this.loading = false;
            }
            const existing = this.todaySod();
            if (existing) {
                this.focusNote = existing.focus_note || '';
                (existing.tasks || []).forEach((t) => {
                    this.$set(this.taskSelections, t.task_id, {
                        selected: true,
                        priority_tier: t.priority_tier || 2,
                        was_added_inline: !!t.was_added_inline,
                    });
                });
            } else {
                this.focusNote = '';
            }
            // taskSelections was reset to {} above — re-seed every other
            // available (not-yet-selected) task now that it's cleared, since the
            // availableTasks watcher above only fires on a genuine list change.
            this.seedMissingSelections(this.availableTasks);
        },
        async addQuickTask() {
            const name = this.newTaskName.trim();
            if (!name || this.addingTask) return;
            this.addingTask = true;
            try {
                const response = await this.$axios.post('api/tasks/create_task_v2', {
                    tasks: [{
                        name,
                        assignee_ids: [this.get_user.id],
                        status_id: 1,
                        due_date: this.$date().format('YYYY-MM-DD'),
                    }],
                });
                if (response.data.response) {
                    const created = (response.data.data && response.data.data[0]) || null;
                    if (created) {
                        this.inlineCreatedTasks.push(created);
                        this.$set(this.taskSelections, created.id, { selected: true, priority_tier: 1, was_added_inline: true });
                    }
                    this.newTaskName = '';
                    this.refreshTasks();
                } else {
                    this.$toast.error(response.data.message || 'Failed to add task');
                }
            } catch (error) {
                console.error('Error adding quick task:', error);
                this.$toast.error('Failed to add task');
            } finally {
                this.addingTask = false;
            }
        },
        dismiss() {
            this.internalDialog = false;
            this.$emit('dismissed');
        },
        viewHistory() {
            this.dismiss();
            this.$router.push({ path: '/sod-eod/history' });
        },
        // Admin-only support/testing tool — deletes today's SOD row
        // entirely, not a resubmission. Server enforces the admin check too.
        async clearData() {
            if (!window.confirm("Delete today's Start of Day submission? This can't be undone.")) {
                return;
            }
            this.clearing = true;
            try {
                await this.clearSodAction();
                this.$toast.success('Start of Day data cleared');
                await this.resetForm();
            } catch (error) {
                console.error('Error clearing SOD:', error);
                this.$toast.error(error?.response?.data?.message || error.message || 'Failed to clear Start of Day');
            } finally {
                this.clearing = false;
            }
        },
        async submit() {
            const selectedTaskIds = Object.keys(this.taskSelections).filter((id) => this.taskSelections[id].selected);
            if (selectedTaskIds.length === 0 && !this.focusNote.trim()) {
                this.$toast.error("Check off at least one task or add today's focus before starting your day");
                return;
            }
            this.saving = true;
            try {
                const payload = {
                    work_date: this.sodEodStatus()?.work_date,
                    timezone: this.sodEodStatus()?.timezone,
                    focus_note: this.focusNote.trim() || null,
                    client_uuid: this.clientUuid,
                    tasks: selectedTaskIds.map((id) => ({
                        task_id: Number(id),
                        priority_tier: this.taskSelections[id].priority_tier || 2,
                        was_added_inline: !!this.taskSelections[id].was_added_inline,
                    })),
                };
                const result = await this.submitSodAction(payload);
                if (result && result.applied === false) {
                    // Superseded by a newer submission made elsewhere — reflect that
                    // record instead of silently claiming success.
                    this.supersededNotice = result;
                    this.$toast.info('A newer Start of Day submission already exists');
                    const current = result.sod_submission || {};
                    this.focusNote = current.focus_note || '';
                    this.taskSelections = {};
                    (current.tasks || []).forEach((t) => {
                        this.$set(this.taskSelections, t.task_id, {
                            selected: true,
                            priority_tier: t.priority_tier || 2,
                            was_added_inline: !!t.was_added_inline,
                        });
                    });
                    this.seedMissingSelections(this.availableTasks);
                } else {
                    this.$toast.success('Start of Day submitted');
                    this.internalDialog = false;
                    this.$emit('submitted', result);
                }
            } catch (error) {
                console.error('Error submitting SOD:', error);
                this.$toast.error(error?.response?.data?.message || error.message || 'Failed to submit Start of Day');
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style lang="scss">
// Shared SOD/EOD chrome — mirrors the Tracking App's approved mockup
// (rounded card, circular sun/moon badge, tinted info box, pill actions).
// Unscoped so it can also style EodDialog.vue identically without duplicating.
.sod-eod-dialog {
    border-radius: 16px;
}

// SOD-only: Vuetify's scrollable dialog defaults to max-height: 90%, which
// forces an inner scrollbar sooner than needed on normal-height screens.
// Scoped to this dialog specifically (not the shared .sod-eod-dialog class)
// since the equivalent EOD override was reverted per feedback.
.sod-dialog-content {
    max-height: 95vh !important;
}

.sod-eod-card.v-card {
    border-radius: 16px !important;
    overflow: hidden;
}

.sod-eod-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 24px 24px 0;
}

.sod-eod-badge {
    width: 44px;
    height: 44px;
    min-width: 44px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
}

.sod-badge {
    background: #fff3d6;
}

.eod-badge {
    background: #ede7f6;
}

.sod-eod-heading {
    flex: 1 1 auto;
    min-width: 0;
    padding-top: 2px;
}

.sod-eod-title {
    font-size: 18px;
    font-weight: 700;
    color: #222;
}

.sod-eod-subtitle {
    font-size: 13px;
    color: #666;
    margin-top: 2px;
}

.sod-eod-close {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    border: none;
    background: transparent;
    color: #888;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
        background: #f2f3f5;
    }
}

.sod-eod-body {
    padding: 16px 24px 8px !important;
}

.sod-eod-info-box {
    border-radius: 8px;
    padding: 14px;
    margin-bottom: 16px;
}

.sod-info-box {
    background: #eaf7ee;

    .sod-eod-info-title {
        color: #1b5e20;
    }

    .sod-eod-info-text {
        color: #3c6b45;
    }
}

.eod-info-box {
    background: #eaf2fe;

    .sod-eod-info-title {
        color: #1a3e72;
    }
}

.sod-eod-info-title {
    font-weight: 700;
    font-size: 13px;
    margin-bottom: 4px;
    color: #222;
}

.sod-eod-info-text {
    font-size: 12px;
    color: #555;
}

.sod-eod-link {
    background: none;
    border: none;
    color: #2e9e5b;
    font-weight: 600;
    font-size: 13px;
    padding: 0;
    cursor: pointer;
    display: inline-block;

    &:hover {
        color: #1b5e20;
    }
}

.sod-eod-actions {
    padding: 8px 24px 24px !important;
}

.sod-eod-btn-primary.v-btn {
    background-color: #2e9e5b !important;
    color: #fff !important;
    text-transform: none;
    border-radius: 8px;
    font-weight: 600;
}

.sod-eod-btn-secondary.v-btn {
    background-color: #f1f1f3 !important;
    color: #333 !important;
    text-transform: none;
    border-radius: 8px;
    box-shadow: none !important;
    margin-right: 8px;
}

.tier-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 8px;
}
</style>

<style lang="scss" scoped>
.sod-task-row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 6px 0;

    .sod-task-checkbox {
        flex: 1 1 auto;
        min-width: 0;
    }

    .sod-task-priority {
        flex: 0 0 190px;
    }
}

.tier-label {
    font-size: 13px;
}
</style>
